import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Tree, message, Button, Spin, Modal } from 'antd';
import {
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaFolderOpen,
  FaGripVertical,
  FaPlus,
  FaRegFileAlt,
  FaTrashAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AppContext from '../contexts/AppContext';

const DISPLAY_MODE_LABELS = {
  none: '-',
  sidebar: 'Standard',
  overlay: 'Dynamic',
  enhanced: 'Enhanced',
  map: 'Map',
};

const getDisplayModeLabel = (layoutStyle) =>
  DISPLAY_MODE_LABELS[String(layoutStyle || '').toLowerCase()] || String(layoutStyle || '-');

const getNodeIcon = (node) =>
  node?.isLeaf ? (
    <FaRegFileAlt className="h-3.5 w-3.5 text-[#4356f4]" />
  ) : (
    <FaFolderOpen className="h-3.5 w-3.5 text-[#4356f4]" />
  );

const buildTreeData = (node, parentId = null, handlers, quickLinkIds = new Set()) => {
  const { toggleVisibility, handleDelete, handleEdit } = handlers;
  const quickLinked = quickLinkIds.has(node.id);
  const displayMode = getDisplayModeLabel(node.layoutStyle);

  const renderTitle = () => (
    <div className="group grid grid-cols-[minmax(0,1fr)_110px_110px_110px] items-center rounded-lg border border-transparent py-2 pl-1 pr-3 text-[12px] text-[#42516b] transition-all duration-300 ease-out hover:-translate-y-[1px] hover:border-[#dfe5f8] hover:bg-[#fcfdff] hover:shadow-[0_10px_20px_rgba(15,23,42,0.045)]">
      <div
        className="flex min-w-0 cursor-pointer items-center gap-2"
        onClick={() => handleEdit(node.id, node)}
      >
        <span className="shrink-0 text-[#9faac0]">
          <FaGripVertical className="h-3.5 w-3.5" />
        </span>
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#eef2ff] transition-all duration-300 ease-out group-hover:scale-[1.04] group-hover:bg-[#e7edff] group-hover:shadow-[0_8px_16px_rgba(74,92,255,0.10)]">
          {getNodeIcon(node)}
        </span>
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={`truncate font-semibold text-[#1b2b44] ${
              node.isLeaf ? 'text-[12px]' : 'text-[14px]'
            } transition-colors duration-300 ease-out group-hover:text-[#12203a]`}
          >
            {node.name || 'Unnamed'}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {typeof node.visible === 'boolean' && parentId !== null && (
          <button
            type="button"
            className="inline-flex h-8.5 w-8.5 items-center justify-center rounded-md border border-[#dfe5f4] bg-white text-[#8f9bb4] transition-all duration-250 ease-out hover:-translate-y-[1px] hover:border-[#ced7ec] hover:shadow-[0_8px_16px_rgba(15,23,42,0.07)] hover:text-[#334154]"
            onClick={(event) => {
              event.stopPropagation();
              toggleVisibility(node);
            }}
            title={node.visible ? 'Hide' : 'Show'}
          >
            {node.visible ? <FaEye className="h-4 w-4" /> : <FaEyeSlash className="h-4 w-4" />}
          </button>
        )}

        <button
          type="button"
          className="inline-flex h-8.5 w-8.5 items-center justify-center rounded-md border border-[#dbe1ff] bg-[#f5f7ff] text-[#4a5cff] transition-all duration-250 ease-out hover:-translate-y-[1px] hover:border-[#c7d0fa] hover:bg-[#eff2ff] hover:shadow-[0_8px_16px_rgba(74,92,255,0.10)]"
          onClick={(event) => {
            event.stopPropagation();
            handleEdit(node.id, node);
          }}
          title="Edit"
        >
          <FaEdit className="h-4 w-4" />
        </button>

        {parentId !== null && (
          <button
            type="button"
            className="inline-flex h-8.5 w-8.5 items-center justify-center rounded-md border border-[#ffd7df] bg-[#fff4f6] text-[#ff4d6d] transition-all duration-250 ease-out hover:-translate-y-[1px] hover:border-[#ffc1cf] hover:bg-[#ffeef2] hover:shadow-[0_8px_16px_rgba(255,77,109,0.10)]"
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(node.id, parentId);
            }}
            title="Delete"
          >
            <FaTrashAlt className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-center gap-1.5 text-[12px] font-medium text-[#4b5872]">
        <span
          className={`inline-block h-2 w-2 shrink-0 rounded-full ${
            quickLinked ? 'bg-[#23c58b]' : 'bg-[#f04c69]'
          }`}
        />
        {quickLinked ? 'Yes' : 'No'}
      </div>

      <div className="flex justify-center">
        <span className="inline-flex max-w-[90px] items-center truncate rounded-full border border-[#d9e1f6] bg-[#f9fbff] px-2 py-[2px] text-[11px] font-medium text-[#5a6884]">
          {displayMode}
        </span>
      </div>
    </div>
  );

  return {
    title: renderTitle(),
    key: node.id,
    children: Array.isArray(node.attributes)
      ? node.attributes.map((child) => buildTreeData(child, node.id, handlers, quickLinkIds))
      : [],
    dataRef: node,
    parentId,
  };
};

const Content = () => {
  const { token, userInfo, selectedRole } = useContext(AppContext);
  const navigate = useNavigate();

  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const quickLinkIds = useMemo(() => {
    const roleList = userInfo?.roleInfo?.[selectedRole?.type] || [];
    const currentRole = roleList.find((role) => role.id === selectedRole?.id);
    return new Set(currentRole?.landing?.quickLinkIds || []);
  }, [selectedRole?.id, selectedRole?.type, userInfo?.roleInfo]);

  const quickLinkIdsKey = useMemo(
    () => Array.from(quickLinkIds).sort().join('|'),
    [quickLinkIds]
  );

  const fetchTree = async () => {
    if (!token || !userInfo?.user?.email || !selectedRole?.id) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/venue/${selectedRole.id}/content/with-visibility/read`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        message.error('Failed to fetch content tree');
        setLoading(false);
        return;
      }

      const data = await response.json();
      const handlers = { toggleVisibility, handleDelete, handleEdit };
      const rootNode = buildTreeData(data, null, handlers, quickLinkIds);
      setTreeData([rootNode]);

      const collectedKeys = [];
      const traverse = (entry) => {
        collectedKeys.push(entry.key);
        if (Array.isArray(entry.children)) {
          entry.children.forEach(traverse);
        }
      };
      traverse(rootNode);
      setExpandedKeys(collectedKeys);
    } catch (error) {
      console.error('Error fetching content:', error);
      message.error('Network error while fetching content');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    token,
    userInfo?.user?.email,
    selectedRole?.id,
    selectedRole?.type,
    selectedRole?.permissionLevel,
    quickLinkIdsKey,
  ]);

  const handleDelete = (key, parentId) => {
    if (!token || !userInfo?.user?.email) {
      message.error('Missing token or user info');
      return;
    }

    Modal.confirm({
      title: 'Delete Node',
      content: 'Are you sure you want to delete this node?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/content/delete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              email: userInfo.user.email,
              venue_id: selectedRole.id,
              content_id: key,
              content_parent_id: parentId,
            }),
          });

          if (!response.ok) {
            const payload = await response.json();
            message.error(payload.error || 'Failed to delete node');
            return;
          }

          message.success('Entry deleted');
          fetchTree();
        } catch (error) {
          console.error('Delete error:', error);
          message.error('Network or server error, could not delete node');
        }
      },
    });
  };

  const handleAdd = async (parentKey) => {
    if (!token || !userInfo?.user?.email) {
      message.error('Missing token or user info');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/content/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: userInfo.user.email,
          venue_id: selectedRole.id,
          content_parent_id: parentKey,
          isLeaf: true,
          layoutStyle: 'none',
          visible: true,
          name: 'New Entry',
          description: '',
          contact: {},
          mainText: '',
          geoLocation: {},
          iconImage: '',
          bannerImage: '',
          carouselImages: [],
          searchTags: '',
        }),
      });

      const newNode = await response.json();
      if (!response.ok) {
        message.error(newNode.error || 'Failed to add entry');
        return;
      }

      message.success('Node added');
      fetchTree();
    } catch (error) {
      console.error('Add error:', error);
      message.error('Network or server error, could not add entry');
    }
  };

  const handleEdit = (key, nodeData) => {
    navigate(`/detail/${key}`, { state: { content: nodeData } });
  };

  const toggleVisibility = async (nodeData) => {
    if (!token || !userInfo?.user?.email) {
      message.error('Missing token or user info');
      return;
    }

    const key = nodeData.id;
    const newVisible = !nodeData.visible;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/content/visibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: userInfo.user.email,
          venue_id: selectedRole.id,
          content_id: key,
          visible: newVisible,
        }),
      });

      if (!response.ok) {
        const payload = await response.json();
        message.error(payload.error || 'Failed to toggle visibility');
        return;
      }

      message.success('Visibility updated');
      fetchTree();
    } catch (error) {
      console.error('Visibility toggle error:', error);
      message.error('Network or server error, could not toggle visibility');
    }
  };

  const onDrop = async (info) => {
    if (!token || !userInfo?.user?.email) {
      message.error('Missing token or user info, unable to reorder');
      return;
    }

    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPosSegments = info.node.pos.split('-');
    const dropPosition =
      info.dropPosition - Number(dropPosSegments[dropPosSegments.length - 1]);

    const dataCopy = [...treeData];
    const findNodeByKey = (data, key) => {
      for (let index = 0; index < data.length; index += 1) {
        const item = data[index];
        if (item.key === key) {
          return { node: item, parentArr: data, index };
        }
        if (Array.isArray(item.children) && item.children.length > 0) {
          const found = findNodeByKey(item.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    const dragged = findNodeByKey(dataCopy, dragKey);
    if (!dragged) {
      return;
    }

    const dragObj = dragged.node;
    const originalParentId = dragObj.parentId;
    dragged.parentArr.splice(dragged.index, 1);

    let targetParentId = null;
    if (!info.dropToGap) {
      const dropTarget = findNodeByKey(dataCopy, dropKey);
      if (
        dropTarget?.node?.children?.length >= 3 &&
        selectedRole?.permissionLevel === 0
      ) {
        message.error(
          'Your package level does not support adding more than 3 entries inside a directory list. Please contact support to upgrade your package.'
        );
        fetchTree();
        return;
      }

      if (dropTarget) {
        if (!dropTarget.node.children) dropTarget.node.children = [];
        targetParentId = dropTarget.node.key;
        dragObj.parentId = targetParentId;
        dropTarget.node.children.unshift(dragObj);
      }
    } else {
      const dropTarget = findNodeByKey(dataCopy, dropKey);
      if (selectedRole?.permissionLevel === 0) {
        const targetParent = findNodeByKey(dataCopy, dropTarget.node.parentId);
        if (targetParent?.node?.children?.length >= 3) {
          message.error(
            'Your package level does not support adding more than 3 entries inside a directory list. Please contact support to upgrade your package.'
          );
          fetchTree();
          return;
        }
      }

      if (dropTarget) {
        const siblings = dropTarget.parentArr;
        targetParentId =
          dropTarget.parentArr === dataCopy ? null : dropTarget.parentArr[0]?.parentId ?? null;
        dragObj.parentId = targetParentId;

        if (dropPosition === -1) {
          siblings.splice(dropTarget.index, 0, dragObj);
        } else if (dropPosition === 1) {
          siblings.splice(dropTarget.index + 1, 0, dragObj);
        }
      }
    }

    setTreeData(dataCopy);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/content/reorder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tokenEmail: userInfo.user.email,
          venue_id: selectedRole.id,
          dragKey,
          originalParentId,
          dropKey,
          targetParentId,
          dropPosition,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        console.error('Reorder API error:', result);
        message.error(result.error || 'Failed to reorder');
        fetchTree();
        return;
      }

      message.success('Order updated');
      fetchTree();
    } catch (error) {
      console.error('Error reordering content:', error);
      message.error('Network or server error, could not reorder');
      fetchTree();
    }
  };

  const onExpand = (nextExpandedKeys) => {
    setExpandedKeys(nextExpandedKeys);
  };

  return (
    <div className="mx-auto mb-20 w-[92%] max-w-[1380px] py-2">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="max-w-2xl text-[13px] leading-7 text-[#5f6f89]">
              Organise and manage the sections and pages in your digital compendium with the
              existing drag, visibility, edit and delete workflows intact.
            </p>
          </div>

          <Button
            type="primary"
            icon={<FaPlus />}
            className="!mt-1 !h-10 !rounded-xl !border-0 !bg-[#4a5cff] !px-5 !font-semibold !shadow-[0_14px_28px_rgba(74,92,255,0.20)] transition-all duration-200 hover:!-translate-y-0.5 hover:!bg-[#3f50e6] hover:!shadow-[0_18px_34px_rgba(74,92,255,0.26)]"
            onClick={() => {
              if (
                selectedRole?.permissionLevel === 0 &&
                treeData[0]?.children?.length >= 5
              ) {
                message.error(
                  'Your package level does not support adding more than 4 entries. Please contact support to upgrade your package.'
                );
                return;
              }
              if (treeData.length === 0) {
                message.warning('Still loading, please try again later');
                return;
              }
              handleAdd(treeData[0].key);
            }}
          >
            Add Entry
          </Button>
      </div>

      <div className="rounded-[28px] border border-[#e2e7f6] bg-white p-4 shadow-[0_16px_36px_rgba(15,23,42,0.05)]">
        <div className="mb-2 grid grid-cols-[minmax(0,1fr)_110px_110px_110px] rounded-lg bg-[#f8faff] px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#98a4bd]">
          <div>Content Item</div>
          <div className="text-right">Actions</div>
          <div className="text-center">Quick Link</div>
          <div className="text-center">Display Mode</div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Spin size="large" />
          </div>
        ) : (
          <div className="rounded-lg border border-[#e9edf7] bg-white px-1 py-0.5">
            <Tree
              className="cms-content-tree"
              draggable
              blockNode
              treeData={treeData}
              expandedKeys={expandedKeys}
              onExpand={onExpand}
              onDrop={onDrop}
              showLine={{ showLeafIcon: false }}
              allowDrop={({ dropPosition }) => dropPosition !== -1}
            />
          </div>
        )}

        <div className="mt-4 flex justify-end border-t border-[#edf1f8] pt-4">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#96a2bc]">
            <span className="rounded-full border border-[#e7ebf7] bg-[#fafbff] px-3 py-1">Click row to edit</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Content;
